import ApiError, { type IAPIError } from '@a24/av-sdk/api-error'
import { useEffect, useRef } from 'react'
import toast from 'react-hot-toast'

type ToastOptions = {
  ignored?: number[]
  overideMessage?: string
}

const isApiError = (error: any): error is ApiError => {
  return (
    error instanceof ApiError ||
    (error &&
      typeof error === 'object' &&
      'exception' in error &&
      'message' in error)
  )
}

const isError = (error: any): error is Error => {
  return (
    error instanceof Error ||
    (error && typeof error === 'object' && 'message' in error)
  )
}

const getErrorMessage = (error: ApiError | Error): string | undefined => {
  console.debug('getErrorMessage :: error -', error instanceof Error)

  if (isApiError(error)) {
    return error.exception?.message || error.message
  }

  if (isError(error)) {
    return error.message
  }
  return undefined
}

export const useErrorToast = (
  state: IAPIError | any,
  options?: ToastOptions
) => {
  const { ignored = [], overideMessage } = options || {}
  const prevState = useRef<any>(false)

  useEffect(() => {
    if (!state) return

    // Check if the prev state matches the current state
    if (prevState.current === state) {
      return
    }

    if ('exception' in state && ignored.includes(state.exception.number)) {
      return
    }
    prevState.current = state

    const errorMsg = getErrorMessage(state)

    if (errorMsg) {
      toast.error(overideMessage || errorMsg)
    }
  }, [state, ignored, overideMessage])
}

export const useSuccessToast = (state: any, options?: ToastOptions) => {
  const { ignored = [], overideMessage } = options || {}
  const prevState = useRef<any>(false)

  useEffect(() => {
    if (!state) return

    if (prevState.current === state) {
      return
    }

    prevState.current = state
    const errorMsg = getErrorMessage(state)

    if (!errorMsg) {
      toast.success(overideMessage || 'Action completed successfully!')
    }
  }, [state, overideMessage])
}
